// Reusable content cards for the customer support surfaces (support page + widget-parity).
import { useState } from 'react';
import type { DocItem, TourItem, VideoItem } from '../lib/types';

export function DocCard({ doc, onOpen }: { doc: DocItem; onOpen?: (d: DocItem) => void }) {
  // Opens the in-app reader (onOpen) instead of a new tab — the guide surfaces here, in-app.
  // Keeps href for middle-click / accessibility, but the plain click is intercepted.
  return (
    <a
      href={doc.url}
      onClick={(e) => { if (onOpen) { e.preventDefault(); onOpen(doc); } }}
      className="group flex flex-col rounded-xl border border-slate-200 bg-white p-4 transition hover:border-brand hover:shadow-md"
    >
      <div className="mb-2 flex items-center gap-2 text-brand">
        <span aria-hidden>📄</span>
        <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Article</span>
      </div>
      <h3 className="font-medium text-slate-900 group-hover:text-brand">{doc.title}</h3>
      {doc.summary && <p className="mt-1 line-clamp-2 text-sm text-slate-500">{doc.summary}</p>}
      <span className="mt-auto pt-3 text-xs font-medium text-brand">Read article →</span>
    </a>
  );
}

// In-app guide reader: a slide-over panel that iframes the doc so the user never leaves the
// support page. `ss-embed=1` lets the docs site render chrome-free (honored later); until then
// the guide renders in its responsive form. A quiet ↗ still opens the full site in a new tab.
export function DocReader({ doc, onClose }: { doc: DocItem; onClose: () => void }) {
  const sep = doc.url.includes('?') ? '&' : '?';
  const embedUrl = `${doc.url}${sep}ss-embed=1`;
  return (
    <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label={doc.title}>
      <div className="flex-1 bg-slate-900/40" onClick={onClose} />
      <div className="flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl">
        <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3">
          <button onClick={onClose} className="text-sm font-medium text-slate-500 hover:text-brand" aria-label="Close guide">← Back</button>
          <span className="flex-1 truncate text-sm font-semibold text-slate-900">{doc.title}</span>
          <a href={doc.url} target="_blank" rel="noopener noreferrer" title="Open in new tab"
             className="rounded px-2 py-1 text-slate-400 hover:bg-slate-100 hover:text-brand">↗</a>
        </div>
        <iframe src={embedUrl} title={doc.title} referrerPolicy="no-referrer" className="h-full w-full flex-1 border-0" />
      </div>
    </div>
  );
}

export function TourCard({ tour, onStart }: { tour: TourItem; onStart?: (t: TourItem) => void }) {
  return (
    <button
      onClick={() => onStart?.(tour)}
      className="group flex flex-col rounded-xl border border-slate-200 bg-white p-4 text-left transition hover:border-brand hover:shadow-md"
    >
      <div className="mb-2 flex items-center gap-2 text-brand">
        <span aria-hidden>🧭</span>
        <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Guided tour</span>
      </div>
      <h3 className="font-medium text-slate-900 group-hover:text-brand">{tour.title}</h3>
      {tour.summary && <p className="mt-1 line-clamp-2 text-sm text-slate-500">{tour.summary}</p>}
      <span className="mt-auto pt-3 text-xs font-medium text-brand">Start tour →</span>
    </button>
  );
}

export function VideoCard({ video, onPlay }: { video: VideoItem; onPlay?: (v: VideoItem) => void }) {
  const [broken, setBroken] = useState(false);
  return (
    <button
      onClick={() => onPlay?.(video)}
      className="group flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white text-left transition hover:border-brand hover:shadow-md"
    >
      <div className="relative aspect-video w-full bg-slate-100">
        {!broken ? (
          <img
            src={video.poster}
            alt=""
            loading="lazy"
            onError={() => setBroken(true)}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="grid h-full w-full place-items-center text-3xl text-slate-300">🎬</div>
        )}
        <span className="absolute inset-0 grid place-items-center">
          <span className="grid h-12 w-12 place-items-center rounded-full bg-black/55 text-xl text-white transition group-hover:bg-brand">▶</span>
        </span>
      </div>
      <div className="p-4">
        <div className="mb-1 flex items-center gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Video</span>
        </div>
        <h3 className="font-medium text-slate-900 group-hover:text-brand">{video.title}</h3>
        {video.summary && <p className="mt-1 line-clamp-2 text-sm text-slate-500">{video.summary}</p>}
      </div>
    </button>
  );
}

export function VideoModal({ video, onClose }: { video: VideoItem; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-3xl overflow-hidden rounded-xl bg-black" onClick={(e) => e.stopPropagation()}>
        <video src={video.src} poster={video.poster} controls autoPlay className="aspect-video w-full bg-black" />
        <div className="flex items-center justify-between bg-white px-4 py-3">
          <h3 className="font-medium text-slate-900">{video.title}</h3>
          <button onClick={onClose} className="rounded-lg px-3 py-1 text-sm text-slate-500 hover:bg-slate-100">Close</button>
        </div>
      </div>
    </div>
  );
}
